import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/AuthContext";
import { useCalls } from "@/CallsContext";
import { currencySymbol } from "@/lib/callStats";

function timezoneName() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (_err) {
    return "UTC";
  }
}

function todayStamp() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatRate(value, symbol = "$") {
  if (value == null || value === "") return "Not set";
  const n = Number(value);
  if (Number.isNaN(n)) return `${symbol}${value}/min`;
  return `${symbol}${n.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")}/min`;
}

export default function PayRatesPage() {
  const { calls, refreshCalls } = useCalls();
  const symbol = currencySymbol(calls);
  const [companies, setCompanies] = useState([]);
  const [history, setHistory] = useState([]);
  const [companyKey, setCompanyKey] = useState("");
  const [rate, setRate] = useState("");
  const [startsOn, setStartsOn] = useState(todayStamp);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const tz = useMemo(() => timezoneName(), []);
  const selected = companies.find((row) => row.company_key === companyKey);
  const formKey = `${companyKey}|${rate}|${startsOn}`;
  const previewReady = preview && preview._formKey === formKey;

  async function loadCompanies(preferredKey) {
    const res = await api.get("/rates/companies");
    const list = res.data || [];
    setCompanies(list);
    const nextKey = preferredKey || companyKey;
    if (nextKey && list.some((row) => row.company_key === nextKey)) {
      setCompanyKey(nextKey);
    } else if (list[0]) {
      setCompanyKey(list[0].company_key);
    }
  }

  async function loadHistory(key) {
    if (!key) {
      setHistory([]);
      return;
    }
    const res = await api.get("/rates/history", { params: { company_key: key } });
    setHistory(res.data || []);
  }

  useEffect(() => {
    api.get("/rates/companies")
      .then((res) => {
        const list = res.data || [];
        setCompanies(list);
        if (list[0]) setCompanyKey((current) => current || list[0].company_key);
      })
      .catch((err) => setError(apiErrorMessage(err)));
  }, []);

  useEffect(() => {
    loadHistory(companyKey).catch((err) => setError(apiErrorMessage(err)));
    setPreview(null);
    setSuccess("");
  }, [companyKey]);

  async function runPreview() {
    if (!selected) return;
    setBusy("preview");
    setError("");
    setSuccess("");
    try {
      const res = await api.post("/rates/preview", {
        company_key: selected.company_key,
        company_id: selected.company_id,
        company_name: selected.company_name,
        starts_on: startsOn,
        pay_per_minute: rate,
        currency: selected.currency || "USD",
        timezone: tz,
      });
      setPreview({ ...res.data, _formKey: formKey });
    } catch (err) {
      setPreview(null);
      setError(apiErrorMessage(err));
    } finally {
      setBusy("");
    }
  }

  async function apply() {
    if (!selected || !previewReady) return;
    setBusy("apply");
    setError("");
    try {
      const res = await api.post("/rates/apply", {
        company_key: selected.company_key,
        company_id: selected.company_id,
        company_name: selected.company_name,
        starts_on: startsOn,
        pay_per_minute: rate,
        currency: selected.currency || "USD",
        timezone: tz,
        confirm: true,
        request_id: requestId,
      });
      setConfirmOpen(false);
      setRequestId(crypto.randomUUID());
      setPreview(null);
      setSuccess(
        res.data?.duplicate
          ? "That adjustment was already saved. Estimated earnings are up to date."
          : "Rate saved. Matching call estimates were recalculated."
      );
      await Promise.all([loadHistory(companyKey), refreshCalls(), loadCompanies(companyKey)]);
    } catch (err) {
      setError(apiErrorMessage(err));
      setConfirmOpen(false);
    } finally {
      setBusy("");
    }
  }

  function loadHistoryRow(row) {
    setStartsOn(row.starts_on);
    setRate(String(row.pay_per_minute || "").replace(/0+$/, "").replace(/\.$/, "") || row.pay_per_minute);
    setPreview(null);
    setSuccess("");
  }

  return (
    <div className="mpd-page" data-testid="pay-rates-page">
      <div className="mpd-page-head">
        <div>
          <p className="mpd-kicker">Pay Rates</p>
          <h1>Rate adjustment calculator</h1>
          <p className="mpd-sub">
            Change a company’s per-minute rate from an effective date. Calls before that date keep the previous rate. Earnings are estimates, not confirmed company payouts.
          </p>
        </div>
      </div>

      <article className="mpd-card mp-pay-card">
        <div className="mpd-card-head">
          <div>
            <h2>Adjust rate</h2>
            <p>Dates use your timezone: {tz}</p>
          </div>
        </div>
        {error ? <p className="mp-error">{error}</p> : null}
        {success ? <p className="mp-success">{success}</p> : null}
        {companies.length === 0 ? (
          <p className="mp-empty">Sync at least one call, then you can set a company rate here.</p>
        ) : (
          <form
            className="mp-pay-form"
            onSubmit={(event) => {
              event.preventDefault();
              runPreview();
            }}
          >
            <label>
              Company
              <select
                value={companyKey}
                onChange={(event) => setCompanyKey(event.target.value)}
                data-testid="pay-rates-company"
              >
                {companies.map((row) => (
                  <option key={row.company_key} value={row.company_key}>{row.company_name}</option>
                ))}
              </select>
            </label>
            <label>
              Current rate
              <Input
                value={selected ? formatRate(selected.current_pay_per_minute, selected.currency_symbol || symbol) : "—"}
                readOnly
                aria-readonly="true"
              />
            </label>
            <label>
              New rate
              <Input
                value={rate}
                onChange={(event) => {
                  setRate(event.target.value);
                  setPreview(null);
                }}
                placeholder="0.37"
                inputMode="decimal"
                data-testid="pay-rates-new-rate"
              />
            </label>
            <label>
              Effective start date
              <Input
                type="date"
                value={startsOn}
                onChange={(event) => {
                  setStartsOn(event.target.value);
                  setPreview(null);
                }}
                data-testid="pay-rates-effective-date"
              />
            </label>
            <div className="mp-pay-actions">
              <Button type="submit" disabled={Boolean(busy) || !rate} data-testid="pay-rates-preview">
                {busy === "preview" ? "Previewing…" : "Preview Recalculation"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={Boolean(busy) || !previewReady}
                onClick={() => setConfirmOpen(true)}
                data-testid="pay-rates-save"
              >
                Save Adjustment
              </Button>
            </div>
          </form>
        )}
      </article>

      {previewReady ? (
        <article className="mpd-card" data-testid="pay-rates-preview-card">
          <div className="mpd-card-head">
            <div>
              <h2>Preview</h2>
              <p>{selected?.company_name} from {preview.starts_on}</p>
            </div>
          </div>
          <ul className="mp-pay-preview">
            <li><span>Affected calls</span><strong>{preview.affected_calls}</strong></li>
            <li><span>Affected minutes</span><strong>{preview.affected_minutes}</strong></li>
            <li><span>Previous earnings</span><strong>{preview.old_estimated_earnings}</strong></li>
            <li><span>Recalculated earnings</span><strong>{preview.new_estimated_earnings}</strong></li>
            <li><span>Difference</span><strong>{preview.difference}</strong></li>
          </ul>
          <p className="mpd-muted">{preview.estimate_disclaimer}</p>
        </article>
      ) : null}

      <article className="mpd-card">
        <div className="mpd-card-head">
          <div>
            <h2>Rate History</h2>
            <p>Saved periods for this company. To correct a mistake, load a row and save a new rate on the same start date.</p>
          </div>
        </div>
        {history.length === 0 ? (
          <p className="mp-empty">No saved rate periods yet.</p>
        ) : (
          <div className="mp-table-wrap">
            <table className="mp-table">
              <thead>
                <tr>
                  <th>Starts</th>
                  <th>Ends</th>
                  <th>Rate / min</th>
                  <th>Currency</th>
                  <th className="mp-table-action"> </th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id || row.starts_on}>
                    <td>{row.starts_on}</td>
                    <td>{row.ends_on || "Open"}</td>
                    <td>{row.pay_per_minute}</td>
                    <td>{row.currency}</td>
                    <td className="mp-table-action">
                      <Button type="button" variant="outline" size="sm" onClick={() => loadHistoryRow(row)}>
                        Correct
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </article>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save rate from {preview?.starts_on}?</AlertDialogTitle>
            <AlertDialogDescription>
              {previewReady ? (
                <span>
                  {selected?.company_name}: {preview.affected_calls} calls, {preview.affected_minutes} minutes.
                  Estimated earnings {preview.old_estimated_earnings} → {preview.new_estimated_earnings}
                  (difference {preview.difference}). This updates matching synced calls for your account only.
                </span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(busy)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={Boolean(busy)}
              onClick={(event) => {
                event.preventDefault();
                apply();
              }}
            >
              {busy === "apply" ? "Saving…" : "Confirm and save"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
